/**
 * PAPPO — VerificationPage (/verificacao)
 * Verificação de conta Nível 00: stepper email → SMS, reenvio com cooldown,
 * erros inline (não há toast). Ao verificar ambos, segue para o chat.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Mail, Smartphone } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import {
  enviarCodigo,
  confirmarCodigo,
  type VerificationTipo,
} from '../services/verification';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import styles from './VerificationPage.module.css';

const COOLDOWN = 30;

export function VerificationPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const emailOk = !!user?.email_verified;
  const phoneOk = !!user?.phone_verified;
  const step: VerificationTipo | 'done' = !emailOk
    ? 'email'
    : !phoneOk
      ? 'sms'
      : 'done';

  const [codigo, setCodigo] = useState('');
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const sentForStep = useRef<string>('');
  const timerRef = useRef<number | null>(null);

  /* Tudo verificado → segue para o chat */
  useEffect(() => {
    if (step === 'done') navigate('/chat', { replace: true });
  }, [step, navigate]);

  const startCooldown = useCallback(() => {
    setCooldown(COOLDOWN);
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = window.setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) {
          if (timerRef.current) window.clearInterval(timerRef.current);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  }, []);

  const enviar = useCallback(
    async (tipo: VerificationTipo) => {
      setErro('');
      setAviso('');
      const res = await enviarCodigo(tipo);
      if (res.success) {
        setAviso(
          tipo === 'email'
            ? 'Código enviado para o seu email.'
            : 'Código enviado por SMS para o seu telefone.'
        );
        startCooldown();
      } else {
        setErro(res.error ?? 'Não foi possível enviar o código.');
      }
    },
    [startCooldown]
  );

  /* Auto-envia ao entrar num passo (uma vez por passo) */
  useEffect(() => {
    if (step === 'done') return;
    if (sentForStep.current !== step) {
      sentForStep.current = step;
      setCodigo('');
      enviar(step);
    }
  }, [step, enviar]);

  /* Limpa o timer ao desmontar */
  useEffect(
    () => () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    },
    []
  );

  const confirmar = useCallback(async () => {
    if (step === 'done') return;
    setErro('');
    setAviso('');
    setBusy(true);
    const res = await confirmarCodigo(step, codigo.trim());
    setBusy(false);
    if (res.success) {
      setCodigo('');
      await refreshUser(); // recalcula o passo a partir do user atualizado
    } else {
      setErro(res.error ?? 'Código incorreto.');
    }
  }, [step, codigo, refreshUser]);

  if (step === 'done') return null;

  const isEmail = step === 'email';

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.logo}>
          <ShieldCheck size={28} className={styles.logoIcon} />
          <h1 className={styles.title}>Verifique sua conta</h1>
          <p className={styles.subtitle}>
            Para conversar no PAPPO, confirme seu email e telefone.
          </p>
        </div>

        <div className={styles.steps}>
          <span
            className={`${styles.stepDot} ${
              emailOk ? styles.stepDone : isEmail ? styles.stepActive : ''
            }`}
          >
            <Mail size={16} /> Email
          </span>
          <span className={styles.stepSep} />
          <span
            className={`${styles.stepDot} ${
              phoneOk ? styles.stepDone : !isEmail ? styles.stepActive : ''
            }`}
          >
            <Smartphone size={16} /> Telefone
          </span>
        </div>

        <p className={styles.muted}>
          {isEmail
            ? 'Enviámos um código de 6 dígitos para o seu email.'
            : 'Enviámos um código de 6 dígitos por SMS para o seu telefone.'}
        </p>

        {erro && (
          <div className={styles.error} role="alert">
            {erro}
          </div>
        )}
        {aviso && !erro && <div className={styles.info}>{aviso}</div>}

        <Input
          label="Código de verificação"
          inputMode="numeric"
          maxLength={6}
          value={codigo}
          onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ''))}
          placeholder="000000"
        />

        <Button
          fullWidth
          size="lg"
          isLoading={busy}
          onClick={confirmar}
          disabled={codigo.length !== 6}
        >
          Confirmar {isEmail ? 'email' : 'telefone'}
        </Button>

        <button
          type="button"
          className={styles.resend}
          disabled={cooldown > 0}
          onClick={() => enviar(step)}
        >
          {cooldown > 0 ? `Reenviar código (${cooldown}s)` : 'Reenviar código'}
        </button>
      </div>
    </div>
  );
}
